import kvid from 'kvid'; // todo: replace with shared-utils-iso?
import { buildTransport } from "./transport-construction";
import { LogActivity } from "../interfaces";
import { buildDebugHandler } from "../util/logging-support";
const Protocol = 'WranggleRpc-1';
export default class Router {
    constructor(opts) {
        this._pendingRequests = {};
        this._finishedRequestIds = new Set(); // todo: @wranggle/rotating-cache to clear/expire (not very big but a memory leak as written.)
        this._stopped = false;
        this._preparseFilters = [];
        this._onValidatedRequest = opts.onValidatedRequest;
    }
    useTransport(transportOpts, channel) {
        this._channel = channel;
        const transport = this.transport = buildTransport(transportOpts);
        // TODO: close existing transport if present
        transport.endpointSenderId = transport.endpointSenderId || `transport:${kvid(12)}`; // only used for debug output. todo: rename endpointSenderId to transportUid
        if (typeof this.debugHandler !== undefined && typeof transport.debugHandler === 'undefined') {
            transport.debugHandler = this.debugHandler;
        }
        transport.listen(this._onMessage.bind(this), channel);
        // todo: send handshake message?
    }
    abortEndpoint(error) {
        this.stopEndpoint();
        this.stopTransport();
        this.pendingRequestIds().forEach((requestId) => {
            const req = this._pendingRequests[requestId];
            if (req && req.isRsvp()) {
                delete this._pendingRequests[requestId];
                req.abortRequest(error);
            }
        });
    }
    stopEndpoint() {
        this._stopped = true;
    }
    stopTransport() {
        if (this.transport) {
            try {
                this.transport && this.transport.stopTransport();
            }
            catch (err) {
                console.error('Failed to stop WranggleRpc transport', err);
            }
            this.transport = void (0);
        }
    }
    sendRemoteRequest(req) {
        if (req.isRsvp()) {
            this._pendingRequests[req.requestId] = req;
        }
        const requestPayload = req.decoratePayload(this._basePayloadData());
        const receipt = req.flightReceipt();
        setTimeout(() => {
            if (!this.transport) {
                receipt.forceReject({
                    errorCode: 'RpcTransportClosedOrMissing',
                    details: {
                        methodName: req.methodName,
                    }
                });
            }
            else {
                req.markAsSent();
                try {
                    this.transport.sendMessage(requestPayload);
                }
                catch (err) {
                    receipt.forceReject(err);
                }
            }
        }, 0);
        return receipt;
    }
    checkConnectionStatus(opts = {}) {
        // todo: implement (do ping-pong check, making sure router does not call onValidatedRequest)
        // do not Reject for timeout, just mark data as bad connection. (Default timeout? 500?)
        // some data to include: isConnected; response time; lastMessageReceivedAt; pingPongResponseTime; endpoint data (senderId, protocol, channel) for local and for remote; timestamp;
        return Promise.resolve({ todo: 'implement this' });
    }
    routerOpts(opts) {
        const { transport, channel, preparseAllIncomingMessages } = opts;
        if (transport && channel) { // relic from early attempt at overlay fancy api
            this.useTransport(transport, channel);
        }
        if (typeof preparseAllIncomingMessages === 'function' && !this._preparseFilters.find(existing => existing === preparseAllIncomingMessages)) {
            this._preparseFilters.push(preparseAllIncomingMessages);
        }
        if (opts.hasOwnProperty('debug')) {
            this.updateDebugHandler(opts.debug);
        }
        if (opts.senderId) {
            this.setEndpointUid(opts.senderId);
        }
    }
    updateDebugHandler(opts, originSegment) {
        if (typeof opts === 'undefined') {
            return;
        }
        if (!originSegment) {
            originSegment = this.transport ? this.transport.constructor.name : 'WranggleRpc:core';
        }
        this.debugHandler = buildDebugHandler(originSegment, opts || false);
        if (this.transport) {
            this.transport.debugHandler = this.debugHandler;
        }
    }
    pendingRequestIds() {
        return Object.keys(this._pendingRequests);
    }
    setEndpointUid(endpointUid) {
        this._endpointUid = endpointUid;
    }
    getEndpointUid() {
        if (!this._endpointUid) {
            this.setEndpointUid(kvid(8));
        }
        return this._endpointUid;
    }
    get channel() {
        return this._channel;
    }
    _onMessage(payload) {
        if (this._stopped) {
            return;
        }
        if (!this._isForUs(payload)) {
            this._debug(LogActivity.IgnoringPayload, { message: `Ignoring payload intended for other recipient`, payload });
            return;
        }
        let parsedPayload = payload;
        this._preparseFilters.forEach(filter => {
            let current;
            if (parsedPayload === false) {
                this._debug(LogActivity.ReceivedInvalidPayload, { message: `Payload failed to pass optional preparseFilter`, payload });
                return;
            }
            try {
                current = filter.call(null, parsedPayload);
            }
            catch (error) {
                this._debug(LogActivity.ReportError, { message: `Error in preparseAllIncomingMessages filter: ${error.message}`, error });
                current = false;
            }
            if (typeof current === 'object') {
                parsedPayload = current;
            }
            else if (current !== true) {
                parsedPayload = false;
            }
        });
        if (parsedPayload.requestId) {
            this._receiveRequest(parsedPayload);
        }
        else if (parsedPayload.respondingTo) {
            this._receiveResponse(parsedPayload);
        }
    }
    _receiveRequest(payload) {
        const requestId = payload.requestId;
        if (this._finishedRequestIds.has(requestId)) {
            return; // warn/error of duplicate message?
        }
        this._finishedRequestIds.add(requestId);
        this._onValidatedRequest(payload.methodName, payload.userArgs, this.getEndpointUid())
            .then((...resolveArgs) => this._handleRsvp(payload, null, resolveArgs))
            .catch((reason) => this._handleRsvp(payload, reason, []));
    }
    _handleRsvp(requestPayload, error, resolveArgs) {
        if (!requestPayload.rsvp) {
            // todo: log & call debug option
            return;
        }
        if (!this.transport) {
            // todo: log & call debug option
            return;
        }
        const { requestId, methodName } = requestPayload;
        if (error && typeof error.toJSON === 'function') {
            try {
                error = error.toJSON();
            }
            catch (err) {
                console.warn('Error in toJSON of error object', err);
            }
        }
        const response = Object.assign(this._basePayloadData(), {
            methodName,
            error, resolveArgs: resolveArgs,
            senderId: this.getEndpointUid(),
            respondingTo: requestId,
        });
        this.transport.sendMessage(response);
    }
    _receiveResponse(response) {
        const requestId = response.respondingTo;
        const req = this._pendingRequests[requestId];
        if (!req || !req.isRsvp()) { // todo: log/warn?
            return;
        }
        delete this._pendingRequests[requestId];
        req.responseReceived(response.error, ...(response.resolveArgs || []));
    }
    _basePayloadData() {
        return {
            channel: this._channel,
            senderId: this.getEndpointUid(),
            protocol: Protocol,
            transportMeta: {},
        };
    }
    _isForUs(payload) {
        if (typeof payload !== 'object' || payload.protocol !== Protocol) {
            return false;
        }
        if (!payload.senderId || payload.senderId === this.getEndpointUid()) { // ignore echos on channel. todo: option to log/warn?
            return false;
        }
        if (!payload.channel || payload.channel !== this._channel) {
            return false;
        }
        // todo: forEndpoint option? if (payload.forEndpoint && payload.forEndpoint !== senderId) { return false; }
        return true;
    }
    endpointInfo() {
        return {
            senderId: this.getEndpointUid()
        };
    }
    _debug(activity, data) {
        if (!this.debugHandler) {
            return;
        }
        this.debugHandler(Object.assign({
            activity,
            endpointSenderId: this.getEndpointUid(),
            channel: this.channel,
        }, data));
    }
}
export { Protocol };
//# sourceMappingURL=router.js.map